import { getConditionalItem } from '@lemonade-hq/chat-wizard-client-base';
import { devWarn } from '@lemonade-hq/ts-helpers';
import { interpolate, REPLACE_WITH_MAP_DEFAULT_KEY, REPLACE_WITH_MAP_WILDCARD } from '@lemonade-hq/uniclient-chat';
import type { InterpolateContext, UniclientMappedInterpolation } from '@lemonade-hq/uniclient-chat';
import get from 'lodash/get';
import set from 'lodash/set';

export function getMappedInterpolation(
  interpolation: UniclientMappedInterpolation | undefined,
  answers: Record<string, unknown> | Record<string, unknown>[],
  context?: InterpolateContext,
): string {
  if (interpolation == null) return '';
  if (typeof interpolation === 'string') {
    return interpolate(interpolation, answers, { context });
  }

  const { pattern, mapValues, replaceWithMap } = getConditionalItem(interpolation, answers);

  const mappedAnswers =
    mapValues != null
      ? Object.entries(mapValues).reduce(
          (accAnswers, [attribute, map]) => {
            const value = get(accAnswers, attribute) as string | null;
            if (value != null && map != null) {
              if (Array.isArray(value)) {
                set(
                  accAnswers,
                  attribute,
                  value.map(v => interpolate(map[v as string] ?? v, answers, { context })),
                );
              } else if (typeof value === 'object') {
                devWarn(
                  `The value you are trying to map is an object, please make sure you are using the right attribute (received attribute: ${attribute})`,
                );
              } else {
                set(accAnswers, attribute, interpolate(map[value] ?? value, answers, { context }));
              }
            }

            return accAnswers;
          },
          { ...answers },
        )
      : answers;

  if (replaceWithMap != null) {
    const key = interpolate(pattern, mappedAnswers, { context });
    const replacedValue = replaceWithMap[key];

    if (replacedValue != null) return replacedValue;

    const keysWithWildCard = Object.keys(replaceWithMap).filter(k => k.includes(REPLACE_WITH_MAP_WILDCARD));

    const wildCardKey = keysWithWildCard.find(k => {
      const regex = new RegExp(`^${k.replace(REPLACE_WITH_MAP_WILDCARD, '.*')}$`);
      return regex.test(key);
    });

    if (wildCardKey != null) {
      return replaceWithMap[wildCardKey] as string;
    }

    return replaceWithMap[REPLACE_WITH_MAP_DEFAULT_KEY] ?? key;
  }

  return interpolate(pattern, mappedAnswers, { context });
}
